/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Subscription } from 'rxjs';
import { OnDestroy, ChangeDetectorRef, ElementRef, Renderer2, NgZone, HostBinding, Input, Directive } from '@angular/core';
import { ChangedEvent, TextService } from '@typescript-kit/core';
import { ComponentModel, KeyCodes } from '@typescript-kit/view';

import { ClickDirective } from './click.directive';
import { TagsDirective } from './tags.directive';
import { VisibleDirective } from './visible.directive';

// @Injectable({
//   providedIn: 'root'
// })
@Directive({
  selector: '[kitBase]'
})
export class BaseDirective<TModel extends ComponentModel> implements OnDestroy {
  private readonly visibleDirective: VisibleDirective;
  private readonly tagsDirective: TagsDirective;
  private readonly clickDirective: ClickDirective;

  private modelPropertyChangedSubscription: Subscription;
  private _model: TModel;
  private isFocusing: boolean = false;

  protected unlistenFocus: () => void;
  protected unlistenBlur: () => void;
  protected unlistenKeydown: () => void;

  constructor(
    protected changeDetector: ChangeDetectorRef,
    protected elementRef: ElementRef,
    protected renderer: Renderer2,
    protected ngZone: NgZone,
    protected textService: TextService
  ) {
    this.tagsDirective = new TagsDirective(elementRef, renderer);
    this.clickDirective = new ClickDirective(elementRef, renderer, ngZone);
    this.visibleDirective = new VisibleDirective(elementRef);
  }

  get element(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  get focusElement(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  get hostClass(): string {
    return null;
  }

  @Input('kitModel')
  set model(model: TModel) {
    if (!model) {
      // empty model is not allowed, but may be called by empty component selector 'kitModel'
      // in this case model must be set via model property (e. g., by kit component directive)
      return;
    }
    if (this.model === model) {
      return;
    }
    if (!this.model && this.hostClass) {
      const classItems = this.hostClass.split(' ');
      classItems.forEach((classItem) => {
        this.addClass(classItem, '');
      });
    }
    if (this.model) {
      this.modelPropertyChangedSubscription.unsubscribe();
      this.modelPropertyChangedSubscription = null;
    }
    if (model) {
      this.modelPropertyChangedSubscription = model.changedEvent
        .subscribe((event) => this.onModelPropertyChanged(event));
    }
    this._model = model;
    this.onModelChanged(model);
  }

  get model(): TModel {
    return this._model;
  }

  @HostBinding('class.kit-focus')
  get isFocused(): boolean {
    return this.model && this.model.isFocused;
  }

  @HostBinding('class.kit-active')
  get isActive(): boolean {
    return this.model && this.model.isActive;
  }

  @HostBinding('class.kit-disabled')
  get isDisabled(): boolean {
    return this.model && this.model.isDisabled;
  }

  ngOnDestroy(): void {
    this.visibleDirective.ngOnDestroy();
    this.tagsDirective.ngOnDestroy();
    this.clickDirective.ngOnDestroy();
    this.deregisterFocusHandler();
    this.deregisterBlurHandler();
    this.deregisterKeydownHandler();
    if (this.modelPropertyChangedSubscription) {
      this.modelPropertyChangedSubscription.unsubscribe();
      this.modelPropertyChangedSubscription = null;
    }
  }

  protected addClass(value: string, prefix: string = 'app-') {
    if (!value) {
      return;
    }
    this.renderer.addClass(this.element, `${prefix}${value}`);
  }

  protected removeClass(value: string, prefix: string = 'app-') {
    if (!value) {
      return;
    }
    this.renderer.removeClass(this.element, `${prefix}${value}`);
  }

  protected setAttribute(key: string, value: string, prefix: string = 'app-') {
    this.renderer.setAttribute(this.element, `${prefix || ''}${key}`, value);
  }

  protected removeAttribute(key: string, prefix: string = 'app-') {
    this.renderer.removeAttribute(this.element, `${prefix || ''}${key}`);
  }

  protected refreshTags() {
    this.tagsDirective.model = this.model.tags;
  }

  protected refreshTabIndex() {
    if (this.model && this.model.tabIndex !== undefined && this.model.tabIndex !== null) {
      this.renderer.setAttribute(this.focusElement, 'tabIndex', this.model.tabIndex.toString());
      this.registerFocusHandler();
      if (this.model.isFocused) {
        this.focusElement.focus();
      }
    } else {
      if (this.model.isFocused) {
        this.focusElement.blur();
      }
      this.renderer.removeAttribute(this.focusElement, 'tabIndex');
      this.deregisterFocusHandler();
    }
  }

  protected refreshIsDisabled() {
    if (this.model.isDisabled) {
      this.renderer.setAttribute(this.focusElement, `disabled`, '');
    } else {
      this.renderer.removeAttribute(this.focusElement, `disabled`);
    }
  }

  protected refreshIsFocused() {
    if (this.model.isFocused) {
      if (this.isFocusing) {
        console.warn('Recursive focusing element is not allowed. Shared use of component models may cause this behaviour.');
        return;
      }
      this.isFocusing = true;
      this.focusElement.focus();
      this.isFocusing = false;
    } else {
      this.focusElement.blur();
    }
  }

  protected registerFocusHandler() {
    if (this.unlistenFocus) {
      return;
    }
    this.unlistenFocus = this.renderer.listen(this.focusElement, 'focus', (event: FocusEvent) => {
      this.registerKeydownHandler();
      this.registerBlurHandler();
      this.ngZone.run(() => this.onFocus(event));
    });
  }

  protected deregisterFocusHandler() {
    if (!this.unlistenFocus) {
      return;
    }
    this.unlistenFocus();
    this.unlistenFocus = null;
  }

  protected registerBlurHandler() {
    if (this.unlistenBlur) {
      return;
    }
    this.unlistenBlur = this.renderer.listen(this.focusElement, 'blur', (event: Event) => {
      this.deregisterKeydownHandler();
      this.deregisterBlurHandler();
      this.ngZone.run(() => this.onBlur(event));
    });
  }

  protected deregisterBlurHandler() {
    if (!this.unlistenBlur) {
      return;
    }
    this.unlistenBlur();
    this.unlistenBlur = null;
  }

  protected registerKeydownHandler() {
    if (this.unlistenKeydown) {
      return;
    }
    this.unlistenKeydown = this.renderer.listen(this.focusElement, 'keydown', (event: KeyboardEvent) => {
      this.ngZone.run(() => this.onKeydown(event));
    });
  }

  protected deregisterKeydownHandler() {
    if (!this.unlistenKeydown) {
      return;
    }
    this.unlistenKeydown();
    this.unlistenKeydown = null;
  }

  protected onModelChanged(model: TModel) {
    this.visibleDirective.model = model;
    this.refreshTags();
    this.clickDirective.model = model;
    this.refreshTabIndex();
    this.refreshIsDisabled();
    this.refreshIsFocused();
  }

  protected onModelPropertyChanged(event: ChangedEvent) {
    if (event.changes['tags']) {
      this.refreshTags();
    }
    if (event.changes['tabIndex']) {
      this.refreshTabIndex();
    }
    if (event.changes['isDisabled']) {
      this.refreshIsDisabled();
    }
    if (event.changes['isFocused']) {
      this.refreshIsFocused();
    }
    this.changeDetector.markForCheck();
  }

  protected onFocus(event: Event) {
    this.model.isFocused = true;
  }

  protected onBlur(event: Event) {
    this.model.isFocused = false;
  }

  protected onKeydown(event: KeyboardEvent) {
    if (this.model.isDisabled) {
      return;
    }
    if (this.model.onKeydown && this.model.onKeydown(event, this.model)) {
      return;
    }
    switch (event.key) {
      case KeyCodes.BACKSPACE.key:
        this.onBackspace(event);
        break;
      case KeyCodes.TAB.key:
        this.onTab(event);
        break;
      case KeyCodes.ENTER.key:
        this.onEnter(event);
        break;
      case KeyCodes.ESC.key:
        this.onEscape(event);
        break;
      case KeyCodes.SPACE.key:
        this.onSpace(event);
        break;
      case KeyCodes.PAGE_UP.key:
        this.onPageUp(event);
        break;
      case KeyCodes.PAGE_DOWN.key:
        this.onPageDown(event);
        break;
      case KeyCodes.END.key:
        this.onEnd(event);
        break;
      case KeyCodes.HOME.key:
        this.onHome(event);
        break;
      case KeyCodes.LEFT.key:
        this.onArrowLeft(event);
        break;
      case KeyCodes.UP.key:
        this.onArrowUp(event);
        break;
      case KeyCodes.RIGHT.key:
        this.onArrowRight(event);
        break;
      case KeyCodes.DOWN.key:
        this.onArrowDown(event);
        break;
      case KeyCodes.DELETE.key:
        this.onDelete(event);
        break;
    }
  }

  protected onBackspace(event: KeyboardEvent) { }

  protected onTab(event: KeyboardEvent) { }

  protected onEnter(event: KeyboardEvent) { }

  protected onEscape(event: KeyboardEvent) { }

  protected onSpace(event: KeyboardEvent) { }

  protected onPageUp(event: KeyboardEvent) { }

  protected onPageDown(event: KeyboardEvent) { }

  protected onEnd(event: KeyboardEvent) { }

  protected onHome(event: KeyboardEvent) { }

  protected onArrowLeft(event: KeyboardEvent) { }

  protected onArrowUp(event: KeyboardEvent) { }

  protected onArrowRight(event: KeyboardEvent) { }

  protected onArrowDown(event: KeyboardEvent) { }

  protected onDelete(event: KeyboardEvent) { }
}
